import { useMemo } from 'react';

import {
  GiftCouponForStore,
  GiftCouponRedeemStatus,
  GiftCouponShareStatus,
} from '@shared/api/giftCoupon/giftCoupon.interface';
import {
  GiftDeliveryForStore,
  GiftDeliveryShareStatus,
  GiftDeliveryStatus,
} from '@shared/api/giftDelivery/giftDelivery.interface';
import { GiftItemForStore } from '@shared/api/giftItem/giftItem.interface';
import {
  GiftOrderDeliveryMode,
  GiftOrderForStore,
  GiftOrderItem,
  GiftOrderItemType,
} from '@shared/api/giftOrder/giftOrder.interface';
import { Image } from '@shared/api/media/media.interface';

/**
 * 주문(order)에서 파생되는 쿠폰/배송 목록·배송모드 플래그·전달 통계를 계산하는 순수 훅.
 * useGiftOrderShare 에서 분리했다 — 부수효과 없이 order 에만 의존하므로 회귀 위험이 없다.
 */
export function useGiftOrderShareItems(order: GiftOrderForStore | null) {
  // === 쿠폰 목록 (취소/폐기 + 정산 전 추가교환 spawn 제외) ===
  const couponList = useMemo(() => {
    return ((order?.coupons ?? []) as GiftCouponForStore[]).filter((c) => {
      if (
        c.redeemStatus === GiftCouponRedeemStatus.Canceled ||
        c.redeemStatus === GiftCouponRedeemStatus.Disposed
      ) {
        return false;
      }
      // 추가교환 spawn 쿠폰은 정산(추가결제/대체발송) 후에만 고객에게 노출한다.
      // 그 전에는 "서비스가 열어둔" 임시 쿠폰일 뿐 고객이 구매한 정당한 쿠폰이 아니라,
      // 노출하면 구매수량보다 많아 보여 분쟁 소지가 있다.
      if (c.isExtraPaid) {
        const settled =
          c.settledBy === 'inventory' ||
          c.settledBy === 'extraPayment' ||
          c.extraPayment?.isPaid === true;
        if (!settled) return false;
      }
      return true;
    });
  }, [order]);

  // === 배송형 상품 ===
  const deliveryItems = useMemo(() => {
    if (!order?.orderDetails) return [];
    return order.orderDetails.filter(
      (item) => item.type === GiftOrderItemType.Delivery,
    ) as GiftOrderItem<GiftItemForStore, Image>[];
  }, [order]);

  const deliveryMode = order?.deliveryMode;
  const isIndividualDelivery = deliveryMode === GiftOrderDeliveryMode.Individual;
  const isBulkDelivery = deliveryMode === GiftOrderDeliveryMode.Bulk;

  const deliveryList = useMemo(() => {
    return ((order?.deliveries ?? []) as GiftDeliveryForStore[]).filter(
      (d) => d.deliveryStatus !== GiftDeliveryStatus.Canceled,
    );
  }, [order]);

  // === 통계 (재발행/재발급 쿠폰 제외) ===
  const originalCouponList = couponList.filter((c) => !c.isAdditional && !c.isReissued);
  const totalCoupons = originalCouponList.length;
  const sharedCoupons = originalCouponList.filter(
    (c) => c.shareStatus !== GiftCouponShareStatus.NotShared,
  ).length;
  const unsharedCoupons = totalCoupons - sharedCoupons;

  const totalIndividualDeliveries = isIndividualDelivery ? deliveryList.length : 0;
  const sharedDeliveries = isIndividualDelivery
    ? deliveryList.filter((d) => d.shareStatus !== GiftDeliveryShareStatus.NotShared)
        .length
    : 0;
  const unsharedDeliveries = totalIndividualDeliveries - sharedDeliveries;

  const hasShareableItems = couponList.length > 0 || isIndividualDelivery;
  const totalUnshared = unsharedCoupons + unsharedDeliveries;

  return {
    couponList,
    deliveryItems,
    deliveryList,
    isIndividualDelivery,
    isBulkDelivery,
    totalCoupons,
    sharedCoupons,
    unsharedCoupons,
    totalIndividualDeliveries,
    sharedDeliveries,
    unsharedDeliveries,
    hasShareableItems,
    totalUnshared,
  };
}
